import { Column, ColumnEditorOptions, ColumnEvent } from 'primereact/column';
import { tr } from '../../../i18n/i18n';
import { DataTable } from 'primereact/datatable';
import React, { useState, useMemo } from 'react';
import { TableLoadingProgress } from '../..';
import TableSkeletonLoader from '../../skeltons/TableSkeletonLoader';
import { commonBodyStyle, commonHeaderStyle } from '../../../interface/styles';
import { PAGINATION } from '../../../constant/main.constant';
import AppPaginator from '../../atoms/AppPaginator';

// Actual row height including padding (12px top + 12px bottom + content ~24px = 48px)
const ACTUAL_ROW_HEIGHT = 64;
// Skeleton display height (should be smaller than actual row for better UX)
const SKELETON_DISPLAY_HEIGHT = 32;

export interface ColumnConfig<T> {
  header: string | React.ReactNode; // Allow string or JSX for header
  field?: keyof T; // Optional, if using default rendering
  body?: ((row: T) => React.ReactNode) | React.ReactNode; // Either a function OR static JSX
  bodyStyle?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
  editor?: (options: ColumnEditorOptions) => React.ReactNode;
  onCellEditComplete?: (e: ColumnEvent) => void;
}

// Props for the empty state component
interface EmptyStateProps {
  message?: React.ReactNode;
  icon?: React.ReactNode;
}

// Centered empty state component
const TableEmptyState: React.FC<EmptyStateProps> = ({
  message = tr('g.no_data_found', 'No data found'),
  icon,
}) => {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      {icon || (
        <svg
          className="w-16 h-16 text-gray-300 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
          />
        </svg>
      )}
      <p className="text-gray-500 text-base font-medium">{message}</p>
    </div>
  );
};

interface ReusableDataTableProps<T> {
  data: T[];
  columns: ColumnConfig<T>[];
  loading?: boolean;
  totalRecords?: number;
  currentPage?: number;
  onPageChange?: (page: number, rows: number) => void;
  pageSize?: number;
  rowActions?: (rowData: T) => React.ReactNode;
  actionHeaderLabel?: React.ReactNode;
  actionBodyStyle?: React.CSSProperties;
  actionHeaderStyle?: React.CSSProperties;
  showPaginator?: boolean;
  emptyMessage?: React.ReactNode;
  emptyIcon?: React.ReactNode;
  isScrollable?: boolean;
  scrollHeight?: string;
  dataKey?: string;
  editMode?: boolean;
  rowsPerPageOptions?: number[];
  showPageSizeDropdown?: boolean;
  showQuickJump?: boolean;
  actualRowHeight?: number; // Actual row height for min-height calculations
  skeletonDisplayHeight?: number; // Visual skeleton height (should be smaller)
  rowClassName?: (rowData: T) => string; // Row-specific class name callback
  freezeActions?: boolean; // Pin the Actions column to the right while the table scrolls horizontally
}

function AppDataTable<T>({
  data,
  columns,
  loading,
  totalRecords,
  currentPage = 0,
  onPageChange,
  pageSize = 5,
  rowActions,
  actionHeaderLabel,
  showPaginator = true,
  emptyMessage = tr('g.no_data_found', 'No data found'),
  emptyIcon,
  actionBodyStyle,
  actionHeaderStyle,
  isScrollable,
  scrollHeight,
  dataKey = 'id',
  editMode = false,
  rowsPerPageOptions = [5, 10, 20, 40, 60, 80, 100],
  showPageSizeDropdown = true,
  showQuickJump = true,
  actualRowHeight,
  skeletonDisplayHeight,
  rowClassName,
  freezeActions = false,
  ...props
}: ReusableDataTableProps<T>) {
  const [rowsPerPage, setRowsPerPage] = useState<number>(pageSize);

  // When freezing the Actions column, the table must run in scrollable mode so the
  // column can stick to the right edge while the rest scrolls horizontally.
  const scrollable = isScrollable || freezeActions;
  const containerClassName = freezeActions
    ? 'app-datatable-container app-datatable-container--frozen'
    : 'app-datatable-container';

  // Handle page change from paginator
  const handlePageChange = (page: number, rows: number) => {
    if (rows !== rowsPerPage) {
      setRowsPerPage(rows);
    }
    if (onPageChange) {
      onPageChange(page, rows);
    }
  };

  // Calculate minimum height based on actual row height to prevent UI jumps
  const actualHeight = actualRowHeight || ACTUAL_ROW_HEIGHT;
  const skeletonHeight = skeletonDisplayHeight || SKELETON_DISPLAY_HEIGHT;

  // Ensure we reserve space at least for the app's default page size.
  // Use the smaller of data length and rowsPerPage, but never less than default page size.
  const minTableHeight = useMemo(() => {
    const headerHeight = 52;
    const displayedRows = Math.min(data?.length || 0, rowsPerPage);
    const effectiveRows = Math.max(displayedRows, PAGINATION.DEFAULT_PAGE_SIZE);
    return headerHeight + effectiveRows * actualHeight;
  }, [rowsPerPage, actualHeight, data?.length]);

  // Custom empty message component (centered)
  const emptyTemplate = useMemo(() => {
    return <TableEmptyState message={emptyMessage} icon={emptyIcon} />;
  }, [emptyMessage, emptyIcon]);

  // Check if data is empty
  const isEmpty = !data || data.length === 0;

  // Show skeleton loader if:
  // 1. Explicitly loading, OR
  // 2. Data is empty AND totalRecords is undefined/null (initial render, data not fetched yet)
  const shouldShowSkeleton = loading || (isEmpty && totalRecords === undefined);

  // Calculate skeleton height - ensure at least default page size's space
  const skeletonMinHeight = useMemo(() => {
    const headerHeight = 52;
    const skeletonRows = Math.max(rowsPerPage, PAGINATION.DEFAULT_PAGE_SIZE);
    return headerHeight + skeletonRows * actualHeight;
  }, [rowsPerPage, actualHeight]);

  // If loading or initial render, display skeleton loader with consistent min-height
  if (shouldShowSkeleton) {
    return (
      <div
        className={containerClassName}
        style={scrollable ? undefined : { minHeight: `${skeletonMinHeight}px` }}
      >
        <TableSkeletonLoader
          rows={Math.max(pageSize, PAGINATION.DEFAULT_PAGE_SIZE)}
          columns={columns}
          actions={!!rowActions}
          rowHeight={skeletonHeight}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Table with sticky header and consistent min-height */}
      <div
        className={containerClassName}
        style={scrollable ? undefined : { minHeight: `${minTableHeight}px` }}
      >
        <DataTable
          dataKey={dataKey}
          scrollable={scrollable}
          scrollHeight={scrollHeight}
          value={data}
          loading={false}
          totalRecords={totalRecords}
          rows={rowsPerPage}
          editMode={editMode ? 'cell' : null}
          first={currentPage * rowsPerPage}
          loadingIcon={<TableLoadingProgress />}
          tableStyle={{
            minWidth: '30rem',
          }}
          emptyMessage={isEmpty && emptyTemplate}
          rowClassName={rowClassName}
          {...props}
        >
          {columns.map((col, index) => (
            <Column
              key={index}
              field={col.field as string}
              header={col.header}
              body={col.body}
              bodyStyle={{
                ...commonBodyStyle,
                height: actualHeight,
                display: 'table-cell',
                verticalAlign: 'middle',
                ...col.bodyStyle,
              }}
              headerStyle={{
                ...commonHeaderStyle,
                ...col.headerStyle,
              }}
              editor={col.editor}
              onCellEditComplete={col.onCellEditComplete}
            />
          ))}

          {rowActions && (
            <Column
              key="actions"
              header={actionHeaderLabel ?? tr('common.column_actions', 'Actions')}
              body={rowActions}
              frozen={freezeActions}
              alignFrozen="right"
              bodyStyle={{
                ...commonBodyStyle,
                height: actualHeight,
                display: 'table-cell',
                verticalAlign: 'middle',
                ...actionBodyStyle,
              }}
              headerStyle={{
                ...actionHeaderStyle,
                ...commonHeaderStyle,
              }}
            />
          )}
        </DataTable>
      </div>

      {/* Enhanced Paginator with page size selection and quick navigation */}
      {showPaginator && totalRecords !== undefined && totalRecords > 0 && (
        <AppPaginator
          currentPage={currentPage}
          pageSize={rowsPerPage}
          total={totalRecords}
          onPageChange={handlePageChange}
          rowsPerPageOptions={rowsPerPageOptions}
          showPageSizeDropdown={showPageSizeDropdown}
          showQuickJump={showQuickJump}
        />
      )}
    </div>
  );
}

export default AppDataTable;
